import { useRef, useEffect, useCallback } from 'react';

export default function ClickSpark({
  sparkColor = '#0e7c72',
  sparkSize = 9,
  sparkRadius = 16,
  sparkCount = 8,
  duration = 420,
  children,
}) {
  const canvasRef = useRef(null);
  const sparks = useRef([]);
  const raf = useRef(0);
  const start = useRef(null);

  const resize = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = window.innerWidth;
    c.height = window.innerHeight;
  }, []);

  useEffect(() => {
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);

  const easeOut = (t) => 1 - Math.pow(1 - t, 3);

  const draw = useCallback(
    (now) => {
      if (!start.current) start.current = now;
      const c = canvasRef.current;
      const ctx = c && c.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, c.width, c.height);

      sparks.current = sparks.current.filter((s) => {
        const elapsed = now - s.t;
        if (elapsed >= duration) return false;
        const p = easeOut(elapsed / duration);
        const dist = p * sparkRadius;
        const len = sparkSize * (1 - p);
        const x1 = s.x + dist * Math.cos(s.a);
        const y1 = s.y + dist * Math.sin(s.a);
        const x2 = s.x + (dist + len) * Math.cos(s.a);
        const y2 = s.y + (dist + len) * Math.sin(s.a);
        ctx.strokeStyle = sparkColor;
        ctx.globalAlpha = 1 - p;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
        return true;
      });
      ctx.globalAlpha = 1;
      raf.current = requestAnimationFrame(draw);
    },
    [duration, sparkColor, sparkRadius, sparkSize]
  );

  useEffect(() => {
    const reduced =
      typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return undefined;

    const onClick = (e) => {
      const now = performance.now();
      const added = Array.from({ length: sparkCount }, (_, i) => ({
        x: e.clientX,
        y: e.clientY,
        a: (2 * Math.PI * i) / sparkCount,
        t: now,
      }));
      sparks.current.push(...added);
      cancelAnimationFrame(raf.current);
      raf.current = requestAnimationFrame(draw);
    };

    window.addEventListener('click', onClick);
    return () => {
      window.removeEventListener('click', onClick);
      cancelAnimationFrame(raf.current);
    };
  }, [draw, sparkCount]);

  return (
    <>
      <canvas
        ref={canvasRef}
        style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 9999 }}
        aria-hidden
      />
      {children}
    </>
  );
}
